

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../Common/Configurations/APIConfig';

const BusinessProfile = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [salonId, setSalonId] = useState('');
    const [profileData, setProfileData] = useState({
        salonId: '',
        name: '',
        slug: '',
        phone: '',
        email: '',
        address: '',
        timeZone: 'Asia/Calcutta',
        status: true,
        createdAt: ''
    });
    const [formData, setFormData] = useState({ ...profileData });

    useEffect(() => {
        const storedSalonId = localStorage.getItem('salonId');
        if (storedSalonId) {
            setSalonId(storedSalonId);
            fetchBusinessProfile(storedSalonId);
        } else {
            console.error('No salonId found in localStorage');
        }
    }, []);

    const fetchBusinessProfile = async (id) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const apiUrl = `${config.APIACTIVEURL}${config.GETSALONSBYID}/${id}`;
            
            const response = await axios.get(apiUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            let salonData = response.data.data || response.data;

            if (salonData) {
                const formattedData = {
                    salonId: salonData.salonId || id,
                    name: salonData.name || '',
                    slug: salonData.slug || '',
                    phone: salonData.phone || salonData.phoneNumber || '',
                    email: salonData.email || '',
                    address: salonData.address || '',
                    timeZone: salonData.timeZone || 'Asia/Calcutta',
                    status: salonData.status !== undefined ? Boolean(salonData.status) : true,
                    createdAt: salonData.createdAt || ''
                };
                setProfileData(formattedData);
                setFormData(formattedData);
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!salonId) return;

        try {
            setSaving(true);
            const token = localStorage.getItem('token');

            // Construct payload to match exactly what common APIs expect
            const updateData = {
                salonId: salonId,
                name: formData.name,
                slug: formData.slug,
                email: formData.email,
                address: formData.address,
                status: formData.status,
                timeZone: formData.timeZone,
                // Send both names in case the backend expects phoneNumber
                phone: formData.phone,
                phoneNumber: formData.phone 
            };

            const response = await axios.put(
                `${config.APIACTIVEURL}${config.UPDATESALON}`,
                updateData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Check for success: API might return {success: true} OR just the object
            if (response.data.success || response.status === 200 || response.status === 204) {
                const updatedObj = response.data.data || response.data;
                
                // Merge update with existing data to keep fields like createdAt
                const finalData = { 
                    ...profileData, 
                    ...updatedObj,
                    phone: updatedObj.phone || updatedObj.phoneNumber || formData.phone 
                };

                setProfileData(finalData);
                setFormData(finalData);
                setIsEditing(false);
                alert('Business profile updated successfully!');
            } else {
                alert(response.data.message || 'Update failed.');
            }
        } catch (error) {
            console.error('Update Error:', error);
            if (error.response) {
                // This will show you exactly what the server validation error is
                const serverMsg = error.response.data?.message || JSON.stringify(error.response.data);
                alert(`Error: ${serverMsg}`);
            } else {
                alert('Network error. Please check your connection.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({ ...profileData });
        setIsEditing(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="container-fluid py-5 text-center" style={{ paddingTop: '100px' }}>
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2">Loading profile...</p>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4" style={{ paddingTop: '100px' }}>
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-0 py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h3 className="mb-0 fw-bold">Business Profile</h3>
                                <div>
                                    {!isEditing ? (
                                        <button className="btn btn-primary" onClick={() => setIsEditing(true)}>
                                            <i className="ri-edit-line me-1"></i> Edit Profile
                                        </button>
                                    ) : (
                                        <div className="d-flex gap-2">
                                            <button className="btn btn-outline-secondary" onClick={handleCancel} disabled={saving}>
                                                Cancel
                                            </button>
                                            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                                                {saving ? 'Saving...' : 'Save Changes'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-lg-4">
                                        <div className="card border-0 shadow-sm mb-4 text-center p-4">
                                            <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: '120px', height: '120px' }}>
                                                <i className="ri-store-line text-primary" style={{ fontSize: '50px' }}></i>
                                            </div>
                                            {isEditing ? (
                                                <input
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    className="form-control text-center mb-2"
                                                    placeholder="Business Name"
                                                />
                                            ) : (
                                                <h4 className="fw-bold">{profileData.name}</h4>
                                            )}
                                            <span className={`badge ${profileData.status ? 'bg-success' : 'bg-danger'} mx-auto`}>
                                                {profileData.status ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="col-lg-8">
                                        <div className="card border-0 shadow-sm p-4">
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-bold">Email</label>
                                                    {isEditing ? (
                                                        <input name="email" value={formData.email} onChange={handleInputChange} className="form-control" />
                                                    ) : (
                                                        <p>{profileData.email || 'N/A'}</p>
                                                    )}
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-bold">Phone</label>
                                                    {isEditing ? (
                                                        <input name="phone" value={formData.phone} onChange={handleInputChange} className="form-control" />
                                                    ) : (
                                                        <p>{profileData.phone || 'N/A'}</p>
                                                    )}
                                                </div>
                                                <div className="col-12 mb-3">
                                                    <label className="form-label fw-bold">Address</label>
                                                    {isEditing ? (
                                                        <textarea name="address" value={formData.address} onChange={handleInputChange} className="form-control" rows="2" />
                                                    ) : (
                                                        <p>{profileData.address || 'N/A'}</p>
                                                    )}
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-bold">Slug (URL)</label>
                                                    {isEditing ? (
                                                        <input name="slug" value={formData.slug} onChange={handleInputChange} className="form-control" />
                                                    ) : (
                                                        <p>{profileData.slug || 'N/A'}</p>
                                                    )}
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-bold">Created On</label>
                                                    <p>{formatDate(profileData.createdAt)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BusinessProfile;